import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const worldHistoryUnit5Notes: UnitNotes = {
  unitId: "unit-5",
  unitName: "Unit 5: Revolutions (c. 1750 – c. 1900)",
  examWeight: "8–10%",
  focus: "How did political revolutions and the Industrial Revolution transform societies, economies, and global power structures?",
  sections: [
    {
      id: "topic-5-1",
      title: "Topic 5.1: The Enlightenment",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💡 The Shift in Thought</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🔬 Empiricism</h4>
                <p className="text-sm text-slate-700 mt-1">Emphasis shifted from revelation and religious authority to <strong>observation and calculation</strong> (Francis Bacon). Knowledge comes from experience and evidence.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🧠 Rationalism</h4>
                <p className="text-sm text-slate-700 mt-1">The belief that <strong>human reason</strong> could discover natural laws to solve social, political, and economic problems.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📚 Key Philosophers and Ideas</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">🇬🇧 John Locke</h4>
                <p className="text-sm text-slate-700 mt-1">Argued against the "Divine Right of Kings." Proposed <strong>Natural Rights</strong> (life, liberty, property) and the <strong>Social Contract</strong> (people give up some power to government to protect these rights; if government fails, people have the right to revolt).</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border-l-4 border-amber-400">
                <h4 className="font-bold text-amber-900">🇫🇷 Montesquieu</h4>
                <p className="text-sm text-slate-700 mt-1">Proposed <strong>Separation of Powers</strong> (checks and balances) to prevent tyranny. Heavily influenced the US Constitution.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🇫🇷 Voltaire</h4>
                <p className="text-sm text-slate-700 mt-1">Advocated for <strong>civil liberties, religious toleration, and freedom of speech</strong>.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border-l-4 border-pink-400">
                <h4 className="font-bold text-pink-900">🇫🇷 Jean-Jacques Rousseau</h4>
                <p className="text-sm text-slate-700 mt-1">Expanded the Social Contract; emphasized the <strong>"General Will"</strong> of the people as the source of legitimate government.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Impact on Society</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">🗳️ Suffrage</h4>
                <p className="text-sm text-slate-700 mt-1">New political ideas eventually led to the expansion of voting rights: initially to property-owning males → all males → women.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">⛓️ Abolition of Slavery</h4>
                <p className="text-sm text-slate-700 mt-1">Enlightenment thinkers and religious groups (Quakers) argued slavery violated natural rights. Led to the end of the slave trade (early 1800s) and slavery itself (mid-to-late 1800s).</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🚜 End of Serfdom</h4>
                <p className="text-sm text-slate-700 mt-1">Russia emancipated serfs in 1861, driven by the realization that coerced labor hindered industrialization.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👩 Emergent Feminism</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🇬🇧 Mary Wollstonecraft</h4>
                <p className="text-sm text-slate-700 mt-1">Wrote <em>A Vindication of the Rights of Woman</em>, arguing women should have the same education as men.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">🇫🇷 Olympe de Gouges</h4>
                <p className="text-sm text-slate-700 mt-1">Wrote <em>Declaration of the Rights of Woman and of the Female Citizen</em> (1791) in response to the French Revolution's exclusion of women.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🇺🇸 Seneca Falls Conference (1848)</h4>
                <p className="text-sm text-slate-700 mt-1">Organized by Elizabeth Cady Stanton and Lucretia Mott; produced the "Declaration of Sentiments" demanding women's suffrage.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-2",
      title: "Topic 5.2: Nationalism and Revolutions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚔️ The Atlantic Revolutions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              These revolutions were inspired by Enlightenment ideals and a desire for popular sovereignty.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🇺🇸 American Revolution (1776)</h4>
                <div className="text-sm text-slate-700 mt-2 space-y-1">
                  <p><strong>Causes:</strong> "No taxation without representation," Enlightenment ideals (Locke)</p>
                  <p><strong>Outcome:</strong> Independence from Britain, establishment of a democratic republic (Constitution/Bill of Rights). Inspired future revolutions.</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">🇫🇷 French Revolution (1789)</h4>
                <div className="text-sm text-slate-700 mt-2 space-y-1">
                  <p><strong>Causes:</strong> Debt from wars, unequal tax burden (The Three Estates), famine</p>
                  <p><strong>Key Documents:</strong> <em>Declaration of the Rights of Man and of the Citizen</em></p>
                  <p><strong>Phases:</strong> Moderate → Radical (Reign of Terror/Robespierre) → Conservative (Napoleon)</p>
                  <p><strong>Outcome:</strong> End of feudalism in France, rise of nationalism, rise of Napoleon (who spread revolutionary legal codes across Europe)</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">🇭🇹 Haitian Revolution (1791)</h4>
                <div className="text-sm text-slate-700 mt-2 space-y-1">
                  <p><strong>Unique Characteristic:</strong> The only successful slave revolt in history</p>
                  <p><strong>Key Leader:</strong> <strong>Toussaint L'Ouverture</strong> (inspired by French Revolution/Enlightenment)</p>
                  <p><strong>Outcome:</strong> Independence from France, establishment of the first Black republic in the Western Hemisphere</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🌎 Latin American Revolutions (early 1800s)</h4>
                <div className="text-sm text-slate-700 mt-2 space-y-1">
                  <p><strong>Causes:</strong> <strong>Creole</strong> dissatisfaction with Spanish Crown (Peninsulares), desire for free trade, Napoleonic invasion of Spain</p>
                  <p><strong>Key Leader:</strong> <strong>Simón Bolívar</strong> (wrote the <em>Jamaica Letter</em> outlining desire for Latin American unity and rights)</p>
                  <p><strong>Outcome:</strong> Independence, but social hierarchies largely remained; failure of "Gran Colombia" (Bolívar's dream of a united South America)</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌐 Rise of Nationalism</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              People began identifying not with a king or religion, but with a "nation" (common language, culture, and history).
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🇩🇪 German Unification (1871)</h4>
                <p className="text-sm text-slate-700 mt-1">Otto von Bismarck used "Blood and Iron" (wars with Denmark, Austria, France) to unite German states under Prussia.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-lime-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🇮🇹 Italian Unification (1871)</h4>
                <p className="text-sm text-slate-700 mt-1">Count Cavour (diplomacy) and Garibaldi (military) united the peninsula.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🇵🇭 Propaganda Movement (Philippines)</h4>
                <p className="text-sm text-slate-700 mt-1">Jose Rizal inspired nationalism against Spanish rule.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🇳🇿 Maori Nationalism</h4>
                <p className="text-sm text-slate-700 mt-1">The New Zealand Wars against the British.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-3",
      title: "Topic 5.3: Industrial Revolution Begins",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏭 Why Great Britain First?</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Location, environment, and social conditions made Britain the birthplace of industrialization.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">💧 Access to Water</h4>
                <p className="text-sm text-slate-700 mt-1">Rivers/canals for transport and water power; island status protected shipping.</p>
              </div>
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900">⛏️ Raw Materials</h4>
                <p className="text-sm text-slate-700 mt-1">Huge deposits of <strong>Coal</strong> and <strong>Iron</strong>.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🌾 Agriculture</h4>
                <p className="text-sm text-slate-700 mt-1">The <em>Agricultural Revolution</em> (crop rotation, seed drill) increased food supply → population boom → more workers.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🏘️ Urbanization</h4>
                <p className="text-sm text-slate-700 mt-1">The <em>Enclosure Movement</em> (fencing off common land) forced peasants off land and into cities, creating a labor pool.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">⚖️ Legal Protection</h4>
                <p className="text-sm text-slate-700 mt-1">Protection of private property and patents encouraged investment.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">💰 Capital</h4>
                <p className="text-sm text-slate-700 mt-1">Wealth from the Atlantic System (slave trade/colonies) provided money to invest.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏢 The Factory System</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🔄 Shift in Production</h4>
                <p className="text-sm text-slate-700 mt-1">Shifted production from the household ("Cottage Industry") to a single location (Factory).</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">⚙️ Specialization of Labor</h4>
                <p className="text-sm text-slate-700 mt-1">Workers focused on one specific task (assembly line style) rather than making a whole product.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🔧 Interchangeable Parts</h4>
                <p className="text-sm text-slate-700 mt-1">(Eli Whitney) Standardized parts allowing for mass production and easy repair.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-4",
      title: "Topic 5.4: Industrialization Spreads",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Geographic Spread</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🇪🇺 Europe</h4>
                <p className="text-sm text-slate-700 mt-1">Spread quickly to Belgium, France, and Germany (Germany eventually surpassed Britain in steel production).</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🇺🇸 United States</h4>
                <p className="text-sm text-slate-700 mt-1">Became a global industrial power by 1900 due to vast natural resources and immigration (labor).</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🇷🇺 Russia</h4>
                <p className="text-sm text-slate-700 mt-1">State-sponsored. Focused on railroads (Trans-Siberian) and heavy industry (steel).</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">🇯🇵 Japan</h4>
                <p className="text-sm text-slate-700 mt-1">Defensive modernization (Meiji Restoration) to avoid Western domination.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 Decline of Middle Eastern and Asian Manufacturing</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              As the West industrialized, traditional manufacturing centers lost global market share.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🚢 Shipbuilding</h4>
                <p className="text-sm text-slate-700 mt-1">India and Southeast Asia declined due to British mismanagement and the rise of steamships.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🧵 Textiles</h4>
                <p className="text-sm text-slate-700 mt-1">Indian and Egyptian cotton textile production collapsed because they could not compete with cheap, mass-produced British textiles.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-5",
      title: "Topic 5.5: Technology of the Industrial Age",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ The Two Stages</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🏭 First Industrial Revolution</h4>
                <p className="text-sm text-slate-700 mt-1">Steam, Iron, Textiles.</p>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">⚙️ Second Industrial Revolution (late 1800s)</h4>
                <p className="text-sm text-slate-700 mt-1">Steel, Chemicals, Precision Machinery, Electronics.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔥 Fossil Fuels and Steam Power</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">💨 Steam Engine</h4>
                <p className="text-sm text-slate-700 mt-1">(James Watt) Allowed factories to be built anywhere (not just near rivers) and powered ships/trains.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🛢️ Internal Combustion Engine</h4>
                <p className="text-sm text-slate-700 mt-1">Led to the use of oil/petroleum.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 Transportation and Communication</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🚂 Railroads</h4>
                <p className="text-sm text-slate-700 mt-1">Facilitated migration to cities, transport of raw materials to factories, and finished goods to markets.</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border border-teal-200">
                <h4 className="font-bold text-teal-900">🚢 Steamships</h4>
                <p className="text-sm text-slate-700 mt-1">Allowed for travel upstream and independent of wind patterns.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">📡 Telegraph</h4>
                <p className="text-sm text-slate-700 mt-1">Instant communication across distances (trans-Atlantic cable).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-6",
      title: "Topic 5.6: Industrialization: Government's Role",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Government Responses to Industrialization</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🇴🇹 Ottoman Empire</h4>
                <p className="text-sm text-slate-700 mt-1">"Sick Man of Europe." Tried to modernize with the <strong>Tanzimat Reforms</strong> (secular education, updated laws), but faced resistance from religious conservatives (Janissaries) and the Sultan eventually suspended reforms (Hamid II).</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🇨🇳 Qing China</h4>
                <div className="text-sm text-slate-700 mt-2 space-y-1">
                  <p><strong>Self-Strengthening Movement:</strong> Attempted to graft Western technology onto Chinese Confucian tradition ("Chinese learning for substance, Western learning for utility"). Largely failed due to internal resistance and corruption.</p>
                  <p>Defeat in the Sino-Japanese War showed the failure of modernization.</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">🇯🇵 Japan (Meiji Restoration)</h4>
                <div className="text-sm text-slate-700 mt-2 space-y-1">
                  <p>Unlike China/Ottomans, Japan successfully modernized.</p>
                  <p>Ended the Tokugawa Shogunate/feudalism.</p>
                  <p><strong>State-Sponsored Industrialization:</strong> The government built factories and sold them to wealthy families (<strong>Zaibatsu</strong>).</p>
                  <p>Adopted Western military, legal, and educational systems.</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🇪🇬 Egypt</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Muhammad Ali:</strong> Semi-independent leader who modernized the military and established a state-sponsored cotton textile industry to compete with Europe.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-7",
      title: "Topic 5.7: Economic Developments and Innovations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💼 Economic Theories</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">👑 Mercantilism (Old)</h4>
                <p className="text-sm text-slate-700 mt-1">Government control, zero-sum game, hoarding gold.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">📈 Laissez-Faire Capitalism (New)</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Adam Smith</strong> (<em>Wealth of Nations</em>). Free markets, supply and demand, minimal government intervention.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏢 Corporate Structures</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">📊 Corporations</h4>
                <p className="text-sm text-slate-700 mt-1">Businesses owned by stockholders.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">⚖️ Limited Liability</h4>
                <p className="text-sm text-slate-700 mt-1">Shareholders are only liable for the money they invest, not the company's debts. Encouraged investment.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🌐 Transnational Businesses</h4>
                <p className="text-sm text-slate-700 mt-1">Companies that operate across national borders. <em>Examples:</em> <strong>HSBC</strong> (Hong Kong and Shanghai Banking Corporation) and <strong>Unilever</strong>.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛍️ Consumerism</h3>
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Rise of the middle class led to more disposable income. Rise of leisure activities (biking, professional sports) and advertising.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-8",
      title: "Topic 5.8: Reactions to the Industrial Economy",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✊ Labor Reform</h3>
            <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">Workers formed <strong>Labor Unions</strong> to bargain for better wages, hours, and conditions. Resulted in minimum wage laws, limits on child labor, and the 8-hour workday.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔴 Radical Ideologies</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🎯 Utilitarianism</h4>
                <p className="text-sm text-slate-700 mt-1">(John Stuart Mill) "The greatest good for the greatest number." Sought moderate reforms.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🚩 Socialism</h4>
                <p className="text-sm text-slate-700 mt-1">The "means of production" (factories, mines) should be owned by the public/state.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">☭ Communism</h4>
                <div className="text-sm text-slate-700 mt-2 space-y-1">
                  <p><strong>Karl Marx</strong> wrote <em>The Communist Manifesto</em>.</p>
                  <p>Argued history is a class struggle: <strong>Bourgeoisie</strong> (owners) vs. <strong>Proletariat</strong> (workers).</p>
                  <p>Predicted violent revolution where workers seize means of production and create a classless society.</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-black/5 to-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900">⚫ Anarchism</h4>
                <p className="text-sm text-slate-700 mt-1">Believed government itself was the problem; advocated for the abolition of the state (often through assassinations).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-9",
      title: "Topic 5.9: Society and the Industrial Age",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏙️ Urbanization</h3>
            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700"><strong>Cities grew rapidly without planning.</strong> Consequences: Tenements (slums), polluted water (cholera), smog, lack of sanitation.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👥 Social Class Changes</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">💼 Middle Class</h4>
                <p className="text-sm text-slate-700 mt-1">Grew significantly (managers, accountants, lawyers).</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🔨 Working Class</h4>
                <p className="text-sm text-slate-700 mt-1">Low skilled, dangerous conditions, expendable labor.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👩 Gender Roles</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🏭 Working Class Women</h4>
                <p className="text-sm text-slate-700 mt-1">Worked in factories/mines to support the family. Paid less than men.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">🏠 Middle Class Women</h4>
                <p className="text-sm text-slate-700 mt-1">Subject to the <strong>Cult of Domesticity</strong>—the idea that a woman's place was in the home to provide a refuge for the husband.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">✊ Feminism</h4>
                <p className="text-sm text-slate-700 mt-1">Industrialization spurred women to organize for voting rights (Suffrage) as they became more active in the economy.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-10",
      title: "Topic 5.10: Continuity and Change in the Industrial Age",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Continuity</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🌾 Agrarian World</h4>
                <p className="text-sm text-slate-700 mt-1">Most of the world remained agrarian (farming-based) despite pockets of industrialization.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">👨‍👩‍👧 Patriarchy</h4>
                <p className="text-sm text-slate-700 mt-1">Persisted, though challenged by early feminism.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🌍 Western Dominance</h4>
                <p className="text-sm text-slate-700 mt-1">Western dominance over non-industrialized regions continued to grow (Imperialism).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Change</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🏭 Production</h4>
                <p className="text-sm text-slate-700 mt-1">Shift from hand-made to machine-made.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">📊 Demographics</h4>
                <p className="text-sm text-slate-700 mt-1">Massive shift from rural to urban living.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🌍 Environment</h4>
                <p className="text-sm text-slate-700 mt-1">Significant degradation (pollution, extraction of resources).</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">💰 Economy</h4>
                <p className="text-sm text-slate-700 mt-1">Global economy became more interconnected; shift from mercantilism to capitalism.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">👥 Social</h4>
                <p className="text-sm text-slate-700 mt-1">Emergence of distinct Proletariat and Bourgeoisie classes.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default worldHistoryUnit5Notes;